import type { ReactNode } from "react";
import { Body2, BrandGraphic, type BrandGraphicProps, Button, Card, RowLayout, SkeletonBlock, SkeletonText, Title5, useMedia, XStack, YStack } from "@medbillai/ui";
export type CtaGraphic = Pick<BrandGraphicProps, "type" | "indicator">;

// Shared hero presentation for the CTA primary slot.
type CtaHeroCardContent = {
  loading?: false;
  title: string;
  /** String, or rich content (e.g. a highlighted savings amount). */
  description: ReactNode;
  buttonLabel: string;
  onPress: () => void;
  graphic: CtaGraphic;
  /** Optional count/savings/provider pill rendered next to the title. */
  titleBadge?: ReactNode;
};
export type CtaHeroCardProps = {
  testID?: string;
} & (CtaHeroCardContent | {
  loading: true;
});
export function CtaHeroCard(props: CtaHeroCardProps) {
  const media = useMedia();
  const loading = props.loading === true;
  const titleText = <SkeletonText loading={loading} chars={14}>
      <Title5 color="$textDefault">
        {props.loading ? undefined : props.title}
      </Title5>
    </SkeletonText>;
  const titleBadge = props.loading ? null : props.titleBadge;
  // Mobile stacks the badge above the title — beside it, "Sutter Health"
  // truncates against the narrower width.
  const titleRow = media.gtXs ? <XStack ai="center" gap="$sp1" flexShrink={1}>
      {titleText}
      {titleBadge}
    </XStack> : <YStack gap="$sp1" ai="flex-start" flexShrink={1}>
      {titleBadge}
      {titleText}
    </YStack>;
  const descriptionText = <SkeletonText loading={loading} chars={28}>
      <Body2 color="$textSubtle">
        {props.loading ? undefined : props.description}
      </Body2>
    </SkeletonText>;
  const graphic = props.loading ? <SkeletonBlock width="$si7" height="$si7" radius="$br2" /> : <BrandGraphic {...props.graphic} size="$si7" />;
  const button = props.loading ? <SkeletonBlock width={media.gtXs ? 110 : "100%"} height="$si5.5" radius="$brFull" /> : <Button variant="brand" size="medium" fillWidth={media.gtXs ? false : undefined} onPress={props.onPress}>
      {props.buttonLabel}
    </Button>;
  return <Card variant="outlined" size="large" padding="$sp3" $gtXs={{
    padding: "$sp2.5",
    pl: "$sp1.5"
  }} testID={props.testID} aria-busy={loading || undefined} data-sentry-element="Card" data-sentry-component="CtaHeroCard" data-sentry-source-file="CtaHeroCard.tsx">
      {media.gtXs ? <RowLayout leading={graphic} primary={titleRow} secondary={descriptionText} rowTrailing={button} /> : <YStack gap="$sp2">
          <RowLayout alignItems="flex-start" primary={titleRow} secondary={descriptionText} rowTrailing={graphic} />
          {button}
        </YStack>}
    </Card>;
}